import "./styles.scss";

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "@components/CustomModal";
import { Input, Text, Icon } from "@fragments";
import {
  IconCheck,
  IconX,
  IconBan,
  IconAlertTriangle,
} from "@tabler/icons-react";

import api from "@helpers/api";

// One modal, three modes (§6.7 accept/reject · §6.8 end). `notesRequired` gates
// the submit; `tone` paints the confirm button + the header accent.
// Built from the caller's `t` so the copy follows the active language.
const getActionConfig = (t) => ({
  accept: {
    title: t("actionModal.accept.title"),
    submit: t("actionModal.accept.submit"),
    color: "success",
    tone: "success",
    icon: IconCheck,
    notesLabel: t("actionModal.accept.notesLabel"),
    notesPlaceholder: t("actionModal.accept.notesPlaceholder"),
    notesRequired: false,
    lead: (name) => t("actionModal.accept.lead", { name }),
    toast: t("actionModal.accept.toast"),
  },
  reject: {
    title: t("actionModal.reject.title"),
    submit: t("actionModal.reject.submit"),
    color: "error",
    tone: "error",
    icon: IconX,
    notesLabel: t("actionModal.reject.notesLabel"),
    notesPlaceholder: t("actionModal.reject.notesPlaceholder"),
    notesRequired: true,
    lead: (name) => t("actionModal.reject.lead", { name }),
    toast: t("actionModal.reject.toast"),
  },
  end: {
    title: t("actionModal.end.title"),
    submit: t("actionModal.end.submit"),
    color: "error",
    tone: "error",
    icon: IconBan,
    notesLabel: t("actionModal.end.notesLabel"),
    notesPlaceholder: t("actionModal.end.notesPlaceholder"),
    notesRequired: false,
    warning: t("actionModal.end.warning"),
    lead: (name) => t("actionModal.end.lead", { name }),
    toast: t("actionModal.end.toast"),
  },
});

/**
 * Review / end enrollment actions — §6.7–6.8. Imperative open:
 *   modalRef.current.open({ enrollment, action: 'accept'|'reject'|'end', onDone })
 * Reject requires a reason; end shows a destructive warning. 422 → inline error
 * on the notes field + toast.
 */
export default function EnrollmentActionModal({ modalRef, communityId }) {
  const { t } = useTranslation("mentoringPrograms");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("accept");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const enrollmentRef = useRef(null);
  const onDoneRef = useRef(null);

  modalRef.current = {
    open({ enrollment, action: nextAction, onDone }) {
      enrollmentRef.current = enrollment ?? null;
      onDoneRef.current = onDone;
      setAction(nextAction ?? "accept");
      setNotes("");
      setError("");
      setOpen(true);
    },
  };

  const ACTION_CONFIG = getActionConfig(t);
  const cfg = ACTION_CONFIG[action] ?? ACTION_CONFIG.accept;
  const enrollment = enrollmentRef.current;
  const name = enrollment
    ? enrollment.creator?.name || enrollment.invited_email
    : t("actionModal.creatorFallback");
  const HeadIcon = cfg.icon;

  async function handleSubmit() {
    const trimmed = notes.trim();
    if (cfg.notesRequired && !trimmed) {
      setError(t("actionModal.notesRequired", { field: cfg.notesLabel.toLowerCase() }));
      return;
    }

    setLoading(true);
    setError("");

    // Review/end live under the program scope; list rows and the detail both
    // carry program_id (the list adapter injects it from the route).
    const base = `community/${communityId}/mentoring/programs/${enrollment?.program_id}/enrollments/${enrollment?.id}`;
    const res =
      action === "end"
        ? await api.delete(base, { notes: trimmed || null })
        : await api.patch(`${base}/review`, { action, notes: trimmed || null });

    setLoading(false);

    if (res?.response?.status >= 400) {
      const fieldError = res.response.data?.errors?.notes?.[0];
      if (fieldError) setError(fieldError);
      window.setSnackbar?.(
        res.response.data?.message || t("actionModal.error"),
      );
      return;
    }

    window.setSnackbar?.(cfg.toast);
    onDoneRef.current?.(res.data);
    setOpen(false);
  }

  return (
    <CustomModal
      maxWidth={480}
      open={open}
      handleClose={() => setOpen(false)}
      title={cfg.title}
      bottomButtonSumbitText={cfg.submit}
      mainButtonColor={cfg.color}
      handleSubmit={handleSubmit}
      loading={loading}
      bodyComponent={
        open ? (
          <div className="enr-action-modal">
            <div className={`eam-head eam-head--${cfg.tone}`}>
              <Icon.Sphere children={<HeadIcon size={20} />} />
              <Text.span contrast={8}>{cfg.lead(name)}</Text.span>
            </div>

            {cfg.warning && (
              <div className="eam-warning">
                <IconAlertTriangle size={18} />
                <Text.span contrast={8}>{cfg.warning}</Text.span>
              </div>
            )}

            <Input.Text
              label={cfg.notesLabel}
              value={notes}
              onInput={(v) => {
                setNotes(v);
                if (error) setError("");
              }}
              multiline
              autoFocus
              error={!!error}
              helper={error}
              placeholder={cfg.notesPlaceholder}
            />
          </div>
        ) : null
      }
    />
  );
}
